/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BoardMigrationStatusResponse } from '../../../model/board-migration-status';
import { BaseService } from '../base-service';
import { BoardsApiConfiguration } from '../boards-api-configuration';
import { AddTargetResponse } from '../models/add-target-response';
import { CreateOrUpdateNodeRequest } from '../models/create-or-update-node-request';
import { CreateTargetRequest } from '../models/create-target-request';
import { DeleteNodeResponse } from '../models/delete-node-response';
import { MigrateBoardRequest } from '../models/migrate-board-request';
import { NodeSummary } from '../models/node-summary';
import { UpdateNodeRequest } from '../models/update-node-request';
import { RequestBuilder } from '../request-builder';
import { StrictHttpResponse } from '../strict-http-response';

@Injectable({
  providedIn: 'root',
})
export class BoardsService extends BaseService {
  constructor(config: BoardsApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getNodes
   */
  static readonly GetNodesPath = '/Boards/{boardId}/Nodes';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getNodes()` instead.
   *
   * This method doesn't expect any request body.
   */
  getNodes$Response(params: { organisationId: string; boardId: string; context?: HttpContext }): Observable<
    StrictHttpResponse<{
      [key: string]: NodeSummary;
    }>
  > {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.GetNodesPath, 'get');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<{
            [key: string]: NodeSummary;
          }>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getNodes$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getNodes(params: { organisationId: string; boardId: string; context?: HttpContext }): Observable<{
    [key: string]: NodeSummary;
  }> {
    return this.getNodes$Response(params).pipe(
      map(
        (
          r: StrictHttpResponse<{
            [key: string]: NodeSummary;
          }>
        ) =>
          r.body as {
            [key: string]: NodeSummary;
          }
      )
    );
  }

  /**
   * Path part for operation addOrUpdateNode
   */
  static readonly AddOrUpdateNodePath = '/Boards/{boardGuid}/Nodes/{nodeGuid}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addOrUpdateNode()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addOrUpdateNode$Response(params: {
    organisationId: string;
    boardGuid: string;
    nodeGuid: string;
    context?: HttpContext;
    body: CreateOrUpdateNodeRequest;
  }): Observable<StrictHttpResponse<NodeSummary>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.AddOrUpdateNodePath, 'put');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardGuid', params.boardGuid, {});
      rb.path('nodeGuid', params.nodeGuid, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<NodeSummary>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `addOrUpdateNode$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addOrUpdateNode(params: {
    organisationId: string;
    boardGuid: string;
    nodeGuid: string;
    context?: HttpContext;
    body: CreateOrUpdateNodeRequest;
  }): Observable<NodeSummary> {
    return this.addOrUpdateNode$Response(params).pipe(
      map((r: StrictHttpResponse<NodeSummary>) => r.body as NodeSummary)
    );
  }

  /**
   * Path part for operation addTarget
   */
  static readonly AddTargetPath = '/Boards/{boardId}/Targets';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addTarget()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addTarget$Response(params: {
    organisationId: string;
    boardId: string;
    context?: HttpContext;
    body: CreateTargetRequest;
  }): Observable<StrictHttpResponse<AddTargetResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.AddTargetPath, 'post');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<AddTargetResponse>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `addTarget$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addTarget(params: {
    organisationId: string;
    boardId: string;
    context?: HttpContext;
    body: CreateTargetRequest;
  }): Observable<AddTargetResponse> {
    return this.addTarget$Response(params).pipe(
      map((r: StrictHttpResponse<AddTargetResponse>) => r.body as AddTargetResponse)
    );
  }

  /**
   * Path part for operation deleteNode
   */
  static readonly DeleteNodePath = '/Boards/{boardId}/Nodes/{nodeId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteNode()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteNode$Response(params: {
    organisationId: string;
    _etag: string;
    recursive?: boolean;
    boardId: string;
    nodeId: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<DeleteNodeResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.DeleteNodePath, 'delete');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.query('_etag', params['_etag'], {});
      rb.query('recursive', params.recursive, {});
      rb.path('boardId', params.boardId, {});
      rb.path('nodeId', params.nodeId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<DeleteNodeResponse>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteNode$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteNode(params: {
    organisationId: string;
    _etag: string;
    recursive?: boolean;
    boardId: string;
    nodeId: string;
    context?: HttpContext;
  }): Observable<DeleteNodeResponse> {
    return this.deleteNode$Response(params).pipe(
      map((r: StrictHttpResponse<DeleteNodeResponse>) => r.body as DeleteNodeResponse)
    );
  }

  /**
   * Path part for operation updateNode
   */
  static readonly UpdateNodePath = '/Boards/{boardId}/Nodes/{nodeId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateNode()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateNode$Response(params: {
    organisationId: string;
    boardId: string;
    nodeId: string;
    context?: HttpContext;
    body: UpdateNodeRequest;
  }): Observable<StrictHttpResponse<NodeSummary>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.UpdateNodePath, 'patch');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
      rb.path('nodeId', params.nodeId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<NodeSummary>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `updateNode$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateNode(params: {
    organisationId: string;
    boardId: string;
    nodeId: string;
    context?: HttpContext;
    body: UpdateNodeRequest;
  }): Observable<NodeSummary> {
    return this.updateNode$Response(params).pipe(map((r: StrictHttpResponse<NodeSummary>) => r.body as NodeSummary));
  }

  /**
   * Path part for operation getBoardMigrationStatus
   */
  static readonly GetBoardMigrationStatusPath = '/Boards/{boardId}/migrationstatus';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getBoardMigrationStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBoardMigrationStatus$Response(params: {
    boardId: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<BoardMigrationStatusResponse>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.GetBoardMigrationStatusPath, 'get');
    if (params) {
      rb.path('boardId', params.boardId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<BoardMigrationStatusResponse>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getBoardMigrationStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getBoardMigrationStatus(params: {
    boardId: string;
    context?: HttpContext;
  }): Observable<BoardMigrationStatusResponse> {
    return this.getBoardMigrationStatus$Response(params).pipe(
      map((r: StrictHttpResponse<BoardMigrationStatusResponse>) => r.body as BoardMigrationStatusResponse)
    );
  }

  /**
   * Path part for operation deleteBoard
   */
  static readonly DeleteBoardPath = '/Boards/{boardId}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteBoard()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteBoard$Response(params: {
    organisationId: string;
    boardId: string;
    context?: HttpContext;
  }): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, BoardsService.DeleteBoardPath, 'delete');
    if (params) {
      rb.query('organisationId', params.organisationId, {});
      rb.path('boardId', params.boardId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: params?.context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteBoard$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteBoard(params: { organisationId: string; boardId: string; context?: HttpContext }): Observable<void> {
    return this.deleteBoard$Response(params).pipe(map((r: StrictHttpResponse<void>) => r.body as void));
  }

  /**
   * Handcrafted migrateBoard method
   */
  static readonly MigrateBoardPath = '/Boards/';
  migrateBoard({ boardId, migrationsToRun, dryRun }: MigrateBoardRequest): Observable<any> {
    return this.http.post(
      this.rootUrl + BoardsService.MigrateBoardPath + boardId + '/migration' + (dryRun ? '?dryRun=true' : ''),
      { MigrationsToRun: migrationsToRun },
      { headers: { 'Content-Type': 'application/json' } }
    );
  }
}
